/* Agendamientos - alta/edición de una agenda (TD-11766).
 *
 * Se puede guardar como BORRADOR (solo exige el título y no lo ve nadie) o
 * PUBLICAR (exige vigencia, horarios y lugar/enlace según la modalidad). Las
 * secciones son plegables para que el formulario no abrume: lo esencial queda
 * abierto y las reglas finas cerradas con valores por defecto razonables.
 *
 * Los turnos no se cargan uno por uno: se definen franjas semanales por
 * responsable (por ej. lunes y miércoles de 08:00 a 12:00) y el sistema las
 * divide en turnos según la duración y la pausa.
 */
import { IonButton, IonContent, IonIcon, IonPage, useIonToast } from "@ionic/react";
import { CircularProgress, TextField } from "@material-ui/core";
import MuiAutocomplete from "@material-ui/lab/Autocomplete";
import { addOutline, arrowBack, closeOutline, trashOutline } from "ionicons/icons";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import AgendaBuscadorFuncionario from "src/components/AgendaBuscadorFuncionario";
import AgendaHorarioSemanal, { erroresDelDia } from "src/components/AgendaHorarioSemanal";
import Header from "src/components/Header";
import AgendaAPI, {
  Catalogos,
  Categoria,
  EstadoTema,
  ExcepcionForm,
  FranjaForm,
  ModalidadAgenda,
  PersonaRef,
  PreguntaAgendaForm,
  ResponsableForm,
  TemaPayload,
  TipoAgenda,
  TipoPublico,
} from "src/services/agenda";
import { CATEGORIAS, DIAS_SEMANA, ETIQUETA_PUBLICO, ymdLocal } from "src/utils/agenda";
import { mensajeError } from "src/utils/errores";
import "./Agenda.css";

let ultimaClave = 0;
const nuevaClave = () => ++ultimaClave;

interface Campos {
  titulo: string;
  descripcion: string;
  categoria: Categoria;
  tipo_agenda: TipoAgenda;
  modalidad: ModalidadAgenda;
  lugar: string;
  link_virtual: string;
  link_minutos_antes: string;
  vigencia_desde: string;
  vigencia_hasta: string;
  duracion_minutos: string;
  pausa_minutos: string;
  cupo_por_turno: string;
  apertura_inscripcion: string;
  anticipacion_min_horas: string;
  max_reservas_activas: string;
  max_reservas_total: string;
  cancelacion_min_horas: string;
  requiere_aprobacion: boolean;
  permite_lista_espera: boolean;
  penalizacion_dias: string;
  publico_tipo: TipoPublico;
  notificar_publicacion: boolean;
  recordatorio_1_horas: string;
  recordatorio_2_horas: string;
  notificar_responsable: boolean;
  notificar_correo: boolean;
  encuesta_habilitada: boolean;
  confidencial: boolean;
}

/** Límites numéricos: los mismos que valida el backend (_validar_tema). */
const RANGOS: Array<[keyof Campos, string, number, number]> = [
  ["duracion_minutos", "Duración del turno", 5, 1440],
  ["pausa_minutos", "Pausa entre turnos", 0, 240],
  ["cupo_por_turno", "Cupo por sesión", 1, 1000],
  ["link_minutos_antes", "Mostrar enlace (minutos antes)", 0, 1440],
  ["anticipacion_min_horas", "Anticipación mínima", 0, 720],
  ["max_reservas_activas", "Reservas activas por persona", 1, 50],
  ["max_reservas_total", "Máx. reservas por persona", 1, 100],
  ["cancelacion_min_horas", "Cancelar/reprogramar hasta", 0, 720],
  ["penalizacion_dias", "Penalización por inasistencia", 0, 365],
  ["recordatorio_1_horas", "1er recordatorio", 0, 720],
  ["recordatorio_2_horas", "2do recordatorio", 0, 720],
];

/** Función y no constante: la fecha de hoy se calcula al abrir el formulario. */
const camposIniciales = (): Campos => ({
  titulo: "",
  descripcion: "",
  categoria: "OTRO",
  tipo_agenda: "INDIVIDUAL",
  modalidad: "PRESENCIAL",
  lugar: "",
  link_virtual: "",
  link_minutos_antes: "15",
  vigencia_desde: ymdLocal(),
  vigencia_hasta: "",
  duracion_minutos: "30",
  pausa_minutos: "0",
  cupo_por_turno: "20",
  apertura_inscripcion: "",
  anticipacion_min_horas: "2",
  max_reservas_activas: "1",
  max_reservas_total: "",
  cancelacion_min_horas: "2",
  requiere_aprobacion: false,
  permite_lista_espera: false,
  penalizacion_dias: "",
  publico_tipo: "GLOBAL",
  notificar_publicacion: true,
  recordatorio_1_horas: "24",
  recordatorio_2_horas: "2",
  notificar_responsable: true,
  notificar_correo: false,
  encuesta_habilitada: false,
  confidencial: false,
});

const num = (v: string) => (v.trim() === "" ? null : Number(v));
/** 0 = "sin aviso", igual que vacío. */
const numOpcional = (v: string) => (v.trim() === "" || Number(v) === 0 ? null : Number(v));
const soloDigitos = (v: string) => v.replace(/\D/g, "");

/* ---------------- Pequeños componentes de presentación ---------------- */

const Seccion: React.FC<{ titulo: string; sub?: string; abierta?: boolean; obligatoria?: boolean }> = ({
  titulo,
  sub,
  abierta,
  obligatoria,
  children,
}) => (
  <details className="agenda-card agenda-seccion" open={abierta}>
    <summary>
      <div>
        <p className="agenda-card-titulo">
          {titulo}
          {obligatoria && <span className="agenda-tag-obligatoria">Obligatoria</span>}
        </p>
        {sub && <p className="agenda-card-sub" style={{ margin: 0 }}>{sub}</p>}
      </div>
    </summary>
    {children}
  </details>
);

const Switch: React.FC<{ checked: boolean; onChange: (v: boolean) => void; titulo: string; ayuda?: string }> = ({
  checked,
  onChange,
  titulo,
  ayuda,
}) => (
  <label className="agenda-switch">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <span>
      {titulo}
      {ayuda && <small>{ayuda}</small>}
    </span>
  </label>
);

/** Asterisco rojo de campo obligatorio (el de los TextField lo pone MUI con `required`). */
const Obligatorio: React.FC = () => <span className="agenda-obligatorio" aria-label="obligatorio"> *</span>;

/** Explicación corta debajo de una opción. */
const Ayuda: React.FC = ({ children }) => <small className="agenda-ayuda">{children}</small>;

const AYUDA_TIPO: Record<TipoAgenda, string> = {
  INDIVIDUAL: "Cada turno es para una sola persona. Ej.: una consulta con la nutricionista.",
  GRUPAL: "Varias personas se anotan en el mismo horario hasta completar el cupo. Ej.: una charla o un taller.",
};

const AYUDA_MODALIDAD: Record<ModalidadAgenda, string> = {
  PRESENCIAL: "La cita es en un lugar físico.",
  VIRTUAL: "La cita es por videollamada: la persona ve el enlace para conectarse.",
  HIBRIDA: "La persona puede ir al lugar o conectarse por videollamada.",
};

const AYUDA_PUBLICO: Record<TipoPublico, string> = {
  GLOBAL: "Todos los funcionarios activos ven la agenda y pueden reservar.",
  ADMINISTRATIVOS: "Solo el personal administrativo ve la agenda y puede reservar.",
  OPERARIOS: "Solo los operarios ven la agenda y pueden reservar.",
  LISTADO: "Solo las personas que cargues abajo ven la agenda y pueden reservar.",
};

const ListaPersonas: React.FC<{ personas: PersonaRef[]; onQuitar: (cedula: string) => void }> = ({ personas, onQuitar }) => (
  <div className="agenda-chips envolver" style={{ marginTop: 8 }}>
    {personas.map((p) => (
      <span key={p.cedula} className="agenda-chip" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {p.nombre || p.cedula}
        <IonIcon icon={closeOutline} style={{ cursor: "pointer" }} onClick={() => onQuitar(p.cedula)} />
      </span>
    ))}
  </div>
);

/* ===================================================================== */

const AgendaTemaForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const temaId = id ? Number(id) : null;
  const history = useHistory();
  const [present] = useIonToast();

  const [cargando, setCargando] = useState(Boolean(temaId));
  const [guardando, setGuardando] = useState(false);
  const [estado, setEstado] = useState<EstadoTema | null>(null);
  const [tieneReservas, setTieneReservas] = useState(false);
  const [catalogos, setCatalogos] = useState<Catalogos>({ cargos: [], departamentos: [], unidades: [] });

  const [c, setC] = useState<Campos>(camposIniciales);
  const set = <K extends keyof Campos>(campo: K, valor: Campos[K]) => setC((prev) => ({ ...prev, [campo]: valor }));

  const [responsables, setResponsables] = useState<ResponsableForm[]>([]);
  const [franjas, setFranjas] = useState<FranjaForm[]>([]);
  const [excepciones, setExcepciones] = useState<ExcepcionForm[]>([]);
  const [preguntas, setPreguntas] = useState<PreguntaAgendaForm[]>([]);
  const [listado, setListado] = useState<PersonaRef[]>([]);
  const [excluidas, setExcluidas] = useState<PersonaRef[]>([]);
  const [cargos, setCargos] = useState<string[]>([]);
  const [departamentos, setDepartamentos] = useState<string[]>([]);
  const [unidades, setUnidades] = useState<string[]>([]);
  const [gestores, setGestores] = useState<PersonaRef[]>([]);
  const [creadoPor, setCreadoPor] = useState<string | null>(null);


  useEffect(() => {
    AgendaAPI.catalogos().then(setCatalogos).catch(() => undefined);
    if (!temaId) return;
    (async () => {
      try {
        const t = await AgendaAPI.obtenerGestion(temaId);
        if (!t.rol.puede_gestionar) {
          present({ message: "Solo los gestores pueden editar la agenda", duration: 3000, color: "warning" });
          history.replace(`/agenda/gestion/${temaId}`);
          return;
        }
        if (t.estado === "FINALIZADO") {
          present({ message: "La agenda está finalizada", duration: 3000, color: "warning" });
          history.replace(`/agenda/gestion/${temaId}`);
          return;
        }
        const s = (v: number | string | null | undefined) => (v === null || v === undefined ? "" : String(v));
        setC({
          titulo: t.titulo,
          descripcion: t.descripcion || "",
          categoria: t.categoria || "OTRO",
          tipo_agenda: t.tipo_agenda,
          modalidad: t.modalidad,
          lugar: t.lugar || "",
          link_virtual: t.link_virtual || "",
          link_minutos_antes: s(t.link_minutos_antes),
          vigencia_desde: t.vigencia_desde || "",
          vigencia_hasta: t.vigencia_hasta || "",
          duracion_minutos: s(t.duracion_minutos),
          pausa_minutos: s(t.pausa_minutos),
          cupo_por_turno: s(t.cupo_por_turno),
          apertura_inscripcion: t.apertura_inscripcion ? t.apertura_inscripcion.slice(0, 16).replace(" ", "T") : "",
          anticipacion_min_horas: s(t.anticipacion_min_horas),
          max_reservas_activas: s(t.max_reservas_activas),
          max_reservas_total: s(t.max_reservas_total),
          cancelacion_min_horas: s(t.cancelacion_min_horas),
          requiere_aprobacion: t.requiere_aprobacion,
          permite_lista_espera: t.permite_lista_espera,
          penalizacion_dias: s(t.penalizacion_dias),
          publico_tipo: t.publico_tipo,
          notificar_publicacion: t.notificar_publicacion,
          recordatorio_1_horas: s(t.recordatorio_1_horas),
          recordatorio_2_horas: s(t.recordatorio_2_horas),
          notificar_responsable: t.notificar_responsable,
          notificar_correo: t.notificar_correo,
          encuesta_habilitada: t.encuesta_habilitada,
          confidencial: t.confidencial,
        });
        // Responsables con clave local; franjas y excepciones apuntan a esa clave.
        const claves = new Map<number, number>();
        setResponsables(
          t.responsables.map((r) => {
            const k = nuevaClave();
            claves.set(r.id, k);
            return { ...r, _k: k };
          })
        );
        const clave = (rid: number | null) => (rid === null ? null : claves.get(rid) ?? null);
        setFranjas(t.franjas.map((f) => ({ ...f, _k: nuevaClave(), responsable_k: clave(f.responsable_id) })));
        setExcepciones(
          t.excepciones.map((e) => ({
            _k: nuevaClave(),
            fecha: e.fecha,
            tipo: e.tipo,
            hora_desde: e.hora_desde || "",
            hora_hasta: e.hora_hasta || "",
            responsable_k: clave(e.responsable_id),
            motivo: e.motivo || "",
          }))
        );
        setPreguntas(t.preguntas.map((p) => ({ ...p, _k: nuevaClave() })));
        setListado(t.publico.cedulas);
        setExcluidas(t.publico.excluidas);
        setCargos(t.publico.cargos);
        setDepartamentos(t.publico.departamentos);
        setUnidades(t.publico.unidades);
        setCreadoPor(t.creado_por);
        setGestores(t.gestores.filter((g) => g.cedula !== t.creado_por));
        setEstado(t.estado);
        setTieneReservas((t.estadisticas?.total || 0) > 0);
      } catch (err: any) {
        present({ message: mensajeError(err, "No se pudo cargar la agenda"), duration: 3500, color: "danger" });
        history.replace("/agenda/gestion");
      } finally {
        setCargando(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temaId]);

  const esBorrador = estado === null || estado === "BORRADOR";
  // Con un solo responsable, todos los horarios son suyos (los cargados antes
  // de elegirlo quedan sin responsable y se le asignan acá).
  useEffect(() => {
    if (responsables.length !== 1) return;
    const k = responsables[0]._k;
    setFranjas((prev) =>
      prev.some((f) => f.responsable_k === null) ? prev.map((f) => (f.responsable_k === null ? { ...f, responsable_k: k } : f)) : prev
    );
  }, [responsables]);

  /* ---------------- Responsables ---------------- */
  const agregarExterno = () =>
    setResponsables((prev) => [...prev, { _k: nuevaClave(), tipo: "EXTERNO", nombre: "", empresa: "", cargo: "", telefono: "", correo: "" }]);

  const editarResp = (k: number, cambio: Partial<ResponsableForm>) =>
    setResponsables((prev) => prev.map((r) => (r._k === k ? { ...r, ...cambio } : r)));

  const quitarResp = (k: number) => {
    const quedan = responsables.filter((r) => r._k !== k);
    setResponsables(quedan);
    // Si era el único, su horario se conserva para quien se agregue después
    // (reemplazo de disertante). Si quedan otros, cada uno tiene el suyo.
    setFranjas((prev) =>
      quedan.length === 0
        ? prev.map((f) => (f.responsable_k === k ? { ...f, responsable_k: null } : f))
        : prev.filter((f) => f.responsable_k !== k)
    );
    // Sus bloqueos y fechas extra eran solo suyos: pasarlos a "toda la agenda"
    // le bloquearía el día a los demás.
    setExcepciones((prev) => prev.filter((e) => e.responsable_k !== k));
  };

  /* ---------------- Excepciones ---------------- */
  const agregarExcepcion = (tipo: "BLOQUEO" | "EXTRA") =>
    setExcepciones((prev) => [
      ...prev,
      {
        _k: nuevaClave(),
        fecha: ymdLocal(1),
        tipo,
        hora_desde: tipo === "EXTRA" ? "08:00" : "",
        hora_hasta: tipo === "EXTRA" ? "12:00" : "",
        responsable_k: responsables.length === 1 ? responsables[0]._k : null,
        motivo: "",
      },
    ]);

  const editarExc = (k: number, cambio: Partial<ExcepcionForm>) =>
    setExcepciones((prev) => prev.map((e) => (e._k === k ? { ...e, ...cambio } : e)));

  /* ---------------- Preguntas ---------------- */
  const editarPreg = (k: number, cambio: Partial<PreguntaAgendaForm>) =>
    setPreguntas((prev) => prev.map((p) => (p._k === k ? { ...p, ...cambio } : p)));

  /* ---------------- Guardar ---------------- */
  const validar = (completa: boolean): string | null => {
    if (!c.titulo.trim()) return "Ingresá el título";
    for (const [campo, etiqueta, min, max] of RANGOS) {
      const valor = c[campo] as string;
      if (valor.trim() === "") continue;
      const n = Number(valor);
      if (n < min || n > max) return `${etiqueta}: tiene que estar entre ${min} y ${max}`;
    }
    if (c.tipo_agenda === "GRUPAL" && !(Number(c.cupo_por_turno) >= 1)) return "Indicá el cupo por sesión";
    if (!(Number(c.duracion_minutos) >= 5)) return "La duración mínima de un turno es de 5 minutos";
    for (const r of responsables) {
      if (r.tipo === "EXTERNO" && !r.nombre.trim()) return "Completá el nombre de los responsables externos";
    }
    for (const p of preguntas) {
      if (!p.texto.trim()) return "Hay una pregunta del formulario sin texto";
      if (p.tipo === "OPCION" && p.opciones.filter((o) => o.trim()).length < 2)
        return `La pregunta "${p.texto}" necesita al menos 2 opciones`;
    }
    if (c.vigencia_desde && c.vigencia_hasta && c.vigencia_hasta < c.vigencia_desde)
      return "La fecha de fin es anterior a la de inicio";
    if (responsables.length > 1 && franjas.some((f) => f.responsable_k === null))
      return "Hay horarios sin responsable: revisá el horario de cada uno";
    for (const r of [null, ...responsables.map((x) => x._k)]) {
      for (const d of DIAS_SEMANA) {
        const errores = erroresDelDia(franjas.filter((f) => f.responsable_k === r && f.dia_semana === d.valor), Number(c.duracion_minutos));
        if (errores.length) return `${d.largo}: ${errores[0]}`;
      }
    }
    for (const e of excepciones) {
      if (!e.fecha) return "Hay una excepción sin fecha";
      if (Boolean(e.hora_desde) !== Boolean(e.hora_hasta)) return "En las excepciones completá las dos horas o ninguna";
      if (e.tipo === "EXTRA" && !e.hora_desde) return "Una fecha extra necesita horario";
    }
    if (completa) {
      if (!c.vigencia_desde) return "Indicá la fecha de inicio de la agenda";
      if (excepciones.some((e) => e.fecha < c.vigencia_desde || (c.vigencia_hasta && e.fecha > c.vigencia_hasta)))
        return "Hay fechas bloqueadas o extra fuera de las fechas de la agenda";
      if (!franjas.length && !excepciones.some((e) => e.tipo === "EXTRA"))
        return "Marcá al menos un día con horario de atención";
      if (c.publico_tipo === "LISTADO" && !listado.length) return "El público es un listado: agregá al menos una cédula";
      if (c.modalidad !== "PRESENCIAL" && !c.link_virtual.trim()) return "Indicá el enlace de la reunión virtual";
      if (c.modalidad !== "VIRTUAL" && !c.lugar.trim()) return "Indicá el lugar";
    }
    return null;
  };

  const armarPayload = (publicar: boolean): TemaPayload => {
    const idx = (k: number | null) => (k === null ? null : responsables.findIndex((r) => r._k === k));
    return {
      titulo: c.titulo.trim(),
      descripcion: c.descripcion.trim(),
      categoria: c.categoria,
      tipo_agenda: c.tipo_agenda,
      modalidad: c.modalidad,
      lugar: c.modalidad === "VIRTUAL" ? "" : c.lugar.trim(),
      link_virtual: c.modalidad === "PRESENCIAL" ? "" : c.link_virtual.trim(),
      link_minutos_antes: num(c.link_minutos_antes),
      vigencia_desde: c.vigencia_desde || null,
      vigencia_hasta: c.vigencia_hasta || null,
      duracion_minutos: num(c.duracion_minutos),
      pausa_minutos: num(c.pausa_minutos),
      cupo_por_turno: c.tipo_agenda === "INDIVIDUAL" ? 1 : num(c.cupo_por_turno),
      apertura_inscripcion: c.apertura_inscripcion || null,
      anticipacion_min_horas: num(c.anticipacion_min_horas),
      max_reservas_activas: num(c.max_reservas_activas),
      max_reservas_total: num(c.max_reservas_total),
      cancelacion_min_horas: num(c.cancelacion_min_horas),
      requiere_aprobacion: c.requiere_aprobacion,
      permite_lista_espera: c.permite_lista_espera,
      penalizacion_dias: num(c.penalizacion_dias),
      publico_tipo: c.publico_tipo,
      notificar_publicacion: c.notificar_publicacion,
      recordatorio_1_horas: numOpcional(c.recordatorio_1_horas),
      recordatorio_2_horas: numOpcional(c.recordatorio_2_horas),
      notificar_responsable: c.notificar_responsable,
      notificar_correo: c.notificar_correo,
      encuesta_habilitada: c.encuesta_habilitada,
      confidencial: c.confidencial,
      responsables: responsables.map((r) => ({
        id: r.id ?? null,
        tipo: r.tipo,
        cedula: r.cedula ?? null,
        nombre: r.nombre,
        cargo: r.cargo ?? null,
        empresa: r.empresa ?? null,
        telefono: r.telefono ?? null,
        correo: r.correo ?? null,
      })),
      franjas: franjas.map((f) => ({
        dia_semana: f.dia_semana,
        hora_desde: f.hora_desde,
        hora_hasta: f.hora_hasta,
        responsable_idx: idx(f.responsable_k),
      })),
      excepciones: excepciones.map((e) => ({
        fecha: e.fecha,
        tipo: e.tipo,
        hora_desde: e.hora_desde || null,
        hora_hasta: e.hora_hasta || null,
        responsable_idx: idx(e.responsable_k),
        motivo: e.motivo.trim(),
      })),
      preguntas: preguntas.map((p) => ({
        id: p.id ?? null,
        tipo: p.tipo,
        obligatoria: p.obligatoria,
        texto: p.texto.trim(),
        opciones: p.tipo === "OPCION" ? p.opciones.map((o) => o.trim()).filter(Boolean) : [],
      })),
      publico: {
        cedulas: listado.map((p) => p.cedula),
        excluidas: excluidas.map((p) => p.cedula),
        cargos,
        departamentos,
        unidades,
      },
      gestores: gestores.map((g) => g.cedula),
      publicar,
    };
  };

  const guardar = async (publicar: boolean) => {
    const error = validar(publicar || !esBorrador);
    if (error) {
      present({ message: error, duration: 3500, color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const payload = armarPayload(publicar);
      let res: { info: string; no_encontrados: string[] };
      let destino: number;
      if (temaId) {
        res = await AgendaAPI.actualizar(temaId, payload);
        destino = temaId;
      } else {
        const creado = await AgendaAPI.crear(payload);
        res = creado;
        destino = creado.id;
      }
      const faltantes = res.no_encontrados?.length
        ? ` ${res.no_encontrados.length} cédula(s) del listado no son funcionarios activos y no se guardaron.`
        : "";
      present({ message: (res.info || "Guardado") + faltantes, duration: faltantes ? 6000 : 2500, color: faltantes ? "warning" : "success" });
      AgendaAPI.olvidarAcceso();
      history.replace(`/agenda/gestion/${destino}`);
    } catch (err: any) {
      present({ message: mensajeError(err, "No se pudo guardar"), duration: 4500, color: "danger" });
    } finally {
      setGuardando(false);
    }
  };

  const volverA = temaId ? `/agenda/gestion/${temaId}` : "/agenda/gestion";

  return (
    <IonPage>
      <Header titulo={temaId ? "Editar agenda" : "Nueva agenda"} botonIzq={{ icono: arrowBack, url: volverA }} />
      <IonContent id="agenda-tema-form">
        {cargando ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <CircularProgress />
          </div>
        ) : (
          <div className="agenda-page campos-mui">
            <div className="agenda-aviso info">
              Los campos marcados con <span className="agenda-obligatorio">*</span> son obligatorios para publicar la
              agenda. Para guardarla como borrador alcanza con el título.
            </div>

            {/* ===================== Datos generales ===================== */}
            <Seccion titulo="Datos generales" sub="Lo que ve la persona al elegir la agenda." abierta obligatoria>
              <div className="agenda-form-col">
                <TextField label="Título" required variant="outlined" fullWidth value={c.titulo} inputProps={{ maxLength: 200 }}
                  placeholder="Ej.: Consulta con nutricionista" helperText="Nombre con el que la gente ve la agenda en su app."
                  onChange={(e) => set("titulo", e.target.value)} />
                <TextField label="Descripción" variant="outlined" fullWidth multiline minRows={3} value={c.descripcion}
                  inputProps={{ maxLength: 2000 }} placeholder="Qué es, qué llevar, cómo prepararse..."
                  helperText="Explicá de qué se trata, qué tiene que llevar o cómo prepararse."
                  onChange={(e) => set("descripcion", e.target.value)} />
                <div>
                  <span className="agenda-label">Categoría</span>
                  <select className="agenda-select" value={c.categoria} onChange={(e) => set("categoria", e.target.value as Categoria)}>
                    {(Object.keys(CATEGORIAS) as Categoria[]).map((k) => (
                      <option key={k} value={k}>{CATEGORIAS[k].etiqueta}</option>
                    ))}
                  </select>
                  <Ayuda>Agrupa la agenda y define el color con que se muestra.</Ayuda>
                </div>
                <div className="agenda-form-fila">
                  <div>
                    <span className="agenda-label">Tipo de agenda</span>
                    <select className="agenda-select" value={c.tipo_agenda} disabled={tieneReservas}
                      onChange={(e) => set("tipo_agenda", e.target.value as TipoAgenda)}>
                      <option value="INDIVIDUAL">Turnos individuales (1 a 1)</option>
                      <option value="GRUPAL">Sesiones grupales (con cupo)</option>
                    </select>
                    <Ayuda>{AYUDA_TIPO[c.tipo_agenda]}</Ayuda>
                  </div>
                  <div>
                    <span className="agenda-label">Modalidad</span>
                    <select className="agenda-select" value={c.modalidad} onChange={(e) => set("modalidad", e.target.value as ModalidadAgenda)}>
                      <option value="PRESENCIAL">Presencial</option>
                      <option value="VIRTUAL">Virtual</option>
                      <option value="HIBRIDA">Híbrida</option>
                    </select>
                    <Ayuda>{AYUDA_MODALIDAD[c.modalidad]}</Ayuda>
                  </div>
                </div>
                {tieneReservas && (
                  <div className="agenda-aviso info" style={{ margin: 0 }}>El tipo de agenda no se puede cambiar porque ya tiene reservas.</div>
                )}
                {c.modalidad !== "VIRTUAL" && (
                  <TextField label="Lugar" required variant="outlined" fullWidth value={c.lugar} inputProps={{ maxLength: 300 }}
                    placeholder="Ej.: Sala 2, casa central" helperText="Dónde se hace la cita: sala, oficina o dirección."
                    onChange={(e) => set("lugar", e.target.value)} />
                )}
                {c.modalidad !== "PRESENCIAL" && (
                  <div className="agenda-form-fila">
                    <TextField label="Enlace de la reunión" required variant="outlined" value={c.link_virtual} inputProps={{ maxLength: 500 }}
                      placeholder="https://meet.google.com/..." style={{ flex: "2 1 220px" }}
                      helperText="Enlace de Meet, Zoom o Teams para conectarse."
                      onChange={(e) => set("link_virtual", e.target.value)} />
                    <TextField label="Mostrar enlace (min antes)" variant="outlined" value={c.link_minutos_antes}
                      helperText="Cuántos minutos antes de la cita aparece el enlace. Antes no se ve."
                      inputProps={{ inputMode: "numeric", maxLength: 4 }}
                      onChange={(e) => set("link_minutos_antes", soloDigitos(e.target.value))} />
                  </div>
                )}
              </div>
            </Seccion>

            {/* ===================== Responsables ===================== */}
            <Seccion titulo={`Disertantes / responsables (${responsables.length})`}
              sub="Quién atiende. Con varios, cada uno tiene sus propios horarios." abierta>
              {responsables.map((r, i) => (
                <div key={r._k} className="agenda-item">
                  <div className="agenda-item-header">
                    <span>{r.tipo === "INTERNO" ? "Interno" : "Externo"} #{i + 1}</span>
                    <IonButton fill="clear" size="small" color="danger" onClick={() => quitarResp(r._k)}>
                      <IonIcon slot="icon-only" icon={trashOutline} />
                    </IonButton>
                  </div>
                  {r.tipo === "INTERNO" ? (
                    <div style={{ fontSize: 14 }}>
                      <strong>{r.nombre}</strong> · C.I. {r.cedula}
                      {r.cargo && <div style={{ color: "#6b7280", fontSize: 13 }}>{r.cargo}</div>}
                    </div>
                  ) : (
                    <div className="agenda-form-col" style={{ gap: 10 }}>
                      <div className="agenda-form-fila">
                        <TextField size="small" label="Nombre" required variant="outlined" value={r.nombre}
                          inputProps={{ maxLength: 200 }} helperText="Como lo ve quien reserva."
                          onChange={(e) => editarResp(r._k, { nombre: e.target.value })} />
                        <TextField size="small" label="Profesión / especialidad" variant="outlined" value={r.cargo || ""}
                          inputProps={{ maxLength: 200 }} helperText="Se muestra junto al nombre."
                          onChange={(e) => editarResp(r._k, { cargo: e.target.value })} />
                      </div>
                      <div className="agenda-form-fila">
                        <TextField size="small" label="Empresa / institución" variant="outlined" value={r.empresa || ""}
                          inputProps={{ maxLength: 200 }} helperText="De dónde viene. Opcional."
                          onChange={(e) => editarResp(r._k, { empresa: e.target.value })} />
                        <TextField size="small" label="Teléfono" variant="outlined" value={r.telefono || ""}
                          inputProps={{ maxLength: 50 }} helperText="Para contactarlo. No lo ve quien reserva."
                          onChange={(e) => editarResp(r._k, { telefono: e.target.value })} />
                        <TextField size="small" label="Correo" variant="outlined" value={r.correo || ""}
                          inputProps={{ maxLength: 200 }} helperText="Si activás el aviso por correo, le llegan acá las reservas."
                          onChange={(e) => editarResp(r._k, { correo: e.target.value })} />
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <div className="agenda-form-col">
                <AgendaBuscadorFuncionario
                  label="Agregar responsable interno"
                  helperText="Funcionario que atiende: ve sus citas en la app, puede aprobarlas y marcar asistencia. Buscalo por nombre, cédula o legajo."
                  onElegir={(f) => {
                    if (responsables.some((r) => r.cedula === f.cedula))
                      return present({ message: "Ya está en la lista", duration: 2000, color: "warning" });
                    setResponsables((prev) => [...prev, { _k: nuevaClave(), tipo: "INTERNO", cedula: f.cedula, nombre: f.nombre, cargo: f.cargo }]);
                  }}
                />
                <IonButton fill="outline" onClick={agregarExterno}>
                  <IonIcon slot="start" icon={addOutline} /> Agregar responsable externo
                </IonButton>
                <Ayuda>
                  Externo: alguien de fuera de la empresa (por ej. un profesional contratado). No usa la app; sus citas
                  las manejan los gestores.
                </Ayuda>
              </div>
            </Seccion>

            {/* ===================== Calendario ===================== */}
            <Seccion titulo="Fechas, días y horarios"
              sub="Entre qué fechas se puede reservar, qué días se atiende y en qué horarios (con cortes)." abierta obligatoria>
              <div className="agenda-form-col">
                <div className="agenda-form-fila">
                  <TextField label="Fecha de inicio" required type="date" variant="outlined"
                    value={c.vigencia_desde} InputLabelProps={{ shrink: true }} helperText="Primer día en que hay turnos."
                    onChange={(e) => set("vigencia_desde", e.target.value)} />
                  <TextField label="Fecha de fin" type="date" variant="outlined"
                    value={c.vigencia_hasta} InputLabelProps={{ shrink: true }}
                    inputProps={{ min: c.vigencia_desde || undefined }}
                    error={Boolean(c.vigencia_desde && c.vigencia_hasta && c.vigencia_hasta < c.vigencia_desde)}
                    helperText="Opcional. Último día con turnos; después la agenda se cierra sola. Vacío = sin fecha de fin: queda abierta hasta que la finalices."
                    onChange={(e) => set("vigencia_hasta", e.target.value)} />
                </div>
                <div className="agenda-form-fila">
                  <TextField label="Duración del turno (min)" required variant="outlined" value={c.duracion_minutos}
                    inputProps={{ inputMode: "numeric", maxLength: 4 }} helperText="Cuánto dura cada turno. El horario se divide en turnos de este largo."
                    onChange={(e) => set("duracion_minutos", soloDigitos(e.target.value))} />
                  <TextField label="Pausa entre turnos (min)" variant="outlined" value={c.pausa_minutos}
                    inputProps={{ inputMode: "numeric", maxLength: 3 }} helperText="Tiempo libre entre un turno y el siguiente. 0 = seguidos."
                    onChange={(e) => set("pausa_minutos", soloDigitos(e.target.value))} />
                  {c.tipo_agenda === "GRUPAL" && (
                    <TextField label="Cupo por sesión" required variant="outlined" value={c.cupo_por_turno}
                      inputProps={{ inputMode: "numeric", maxLength: 4 }} helperText="Cuántas personas entran en cada sesión."
                      onChange={(e) => set("cupo_por_turno", soloDigitos(e.target.value))} />
                  )}
                </div>

                <span className="agenda-label" style={{ fontSize: 14, color: "#1f2937", fontWeight: 600, margin: 0 }}>
                  Días y horarios de atención<Obligatorio />
                </span>
                <Ayuda>
                  Marcá al menos un día y su horario. Si hay un corte (por ej. el almuerzo), usá
                  "Agregar corte" para cargar dos tramos: 08:00 a 12:00 y 14:00 a 17:00.
                </Ayuda>
                <AgendaHorarioSemanal
                  franjas={franjas}
                  onChange={setFranjas}
                  responsables={responsables}
                  duracion={Number(c.duracion_minutos) || 0}
                  pausa={Number(c.pausa_minutos) || 0}
                  nuevaClave={nuevaClave}
                />
              </div>
            </Seccion>

            <Seccion titulo={`Fechas bloqueadas y extra (${excepciones.length})`}
              sub="Cambios al horario de un día puntual: días sin atención o días de atención especial.">
              <div className="agenda-aviso info">
                <strong>Bloquear fecha:</strong> ese día (o esas horas) no se atiende. Ej.: un feriado o las vacaciones
                del disertante. Las citas ya tomadas ese día no se cancelan solas.
                <br />
                <strong>Fecha extra:</strong> se atiende un día que normalmente no. Ej.: una jornada especial un sábado.
              </div>
              {excepciones.map((e) => (
                <div key={e._k} className="agenda-item">
                  <div className="agenda-item-header">
                    <span>{e.tipo === "BLOQUEO" ? "Bloqueo" : "Fecha extra"}</span>
                    <IonButton fill="clear" size="small" color="danger" onClick={() => setExcepciones((prev) => prev.filter((x) => x._k !== e._k))}>
                      <IonIcon slot="icon-only" icon={trashOutline} />
                    </IonButton>
                  </div>
                  <div className="agenda-form-fila">
                    <TextField size="small" label="Fecha" required type="date" variant="outlined" value={e.fecha}
                      InputLabelProps={{ shrink: true }} onChange={(ev) => editarExc(e._k, { fecha: ev.target.value })} />
                    <TextField size="small" label={e.tipo === "BLOQUEO" ? "Desde (vacío = todo el día)" : "Desde"} required={e.tipo === "EXTRA"} type="time"
                      variant="outlined" value={e.hora_desde} InputLabelProps={{ shrink: true }} inputProps={{ step: 300 }}
                      onChange={(ev) => editarExc(e._k, { hora_desde: ev.target.value })} />
                    <TextField size="small" label="Hasta" required={e.tipo === "EXTRA"} type="time" variant="outlined" value={e.hora_hasta}
                      InputLabelProps={{ shrink: true }} inputProps={{ step: 300 }}
                      onChange={(ev) => editarExc(e._k, { hora_hasta: ev.target.value })} />
                  </div>
                  <div className="agenda-form-fila" style={{ marginTop: 10 }}>
                    {responsables.length > 1 && (
                      <select className="agenda-select chico" value={e.responsable_k ?? ""}
                        onChange={(ev) => editarExc(e._k, { responsable_k: ev.target.value ? Number(ev.target.value) : null })}>
                        <option value="">{e.tipo === "BLOQUEO" ? "Toda la agenda" : "Sin responsable"}</option>
                        {responsables.map((r) => (
                          <option key={r._k} value={r._k}>{r.nombre || r.cedula}</option>
                        ))}
                      </select>
                    )}
                    <TextField size="small" label="Motivo" variant="outlined" value={e.motivo} inputProps={{ maxLength: 200 }}
                      placeholder="Ej.: Feriado" helperText="Para recordar por qué se cargó. No lo ve quien reserva."
                      onChange={(ev) => editarExc(e._k, { motivo: ev.target.value })} />
                  </div>
                </div>
              ))}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <IonButton fill="outline" onClick={() => agregarExcepcion("BLOQUEO")}>
                  <IonIcon slot="start" icon={addOutline} /> Bloquear fecha
                </IonButton>
                <IonButton fill="outline" onClick={() => agregarExcepcion("EXTRA")}>
                  <IonIcon slot="start" icon={addOutline} /> Fecha extra
                </IonButton>
              </div>
            </Seccion>

            {/* ===================== Reglas ===================== */}
            <Seccion titulo="Reglas de reserva" sub="Anticipación, límites por persona, cancelación y aprobación.">
              <div className="agenda-form-col">
                <TextField label="Apertura de inscripciones" type="datetime-local" variant="outlined" value={c.apertura_inscripcion}
                  InputLabelProps={{ shrink: true }} helperText="Desde cuándo se puede empezar a reservar. Vacío = apenas se publica."
                  onChange={(e) => set("apertura_inscripcion", e.target.value)} />
                <TextField label="Anticipación mínima (horas)" variant="outlined" value={c.anticipacion_min_horas}
                  helperText="Tiempo mínimo antes del turno para poder tomarlo. Ej.: 2 = no se toman turnos que empiezan en menos de 2 horas." inputProps={{ inputMode: "numeric", maxLength: 3 }}
                  onChange={(e) => set("anticipacion_min_horas", soloDigitos(e.target.value))} />
                <div className="agenda-form-fila">
                  <TextField label="Reservas activas por persona" variant="outlined" value={c.max_reservas_activas}
                    inputProps={{ inputMode: "numeric", maxLength: 2 }} helperText="Cuántas citas pendientes puede tener una persona al mismo tiempo."
                    onChange={(e) => set("max_reservas_activas", soloDigitos(e.target.value))} />
                  <TextField label="Máx. reservas por persona" variant="outlined" value={c.max_reservas_total} placeholder="Ej.: 1"
                    helperText="Cuántas citas puede tener cada persona en toda la agenda, contando las ya pasadas. Ej.: 1 = una sola vez. Vacío = sin límite."
                    inputProps={{ inputMode: "numeric", maxLength: 3 }} onChange={(e) => set("max_reservas_total", soloDigitos(e.target.value))} />
                </div>
                <div className="agenda-form-fila">
                  <TextField label="Cancelar/reprogramar hasta (horas antes)" variant="outlined" value={c.cancelacion_min_horas}
                    inputProps={{ inputMode: "numeric", maxLength: 3 }} helperText="Hasta cuántas horas antes la persona puede cancelar o cambiar el horario desde la app."
                    onChange={(e) => set("cancelacion_min_horas", soloDigitos(e.target.value))} />
                  <TextField label="Penalización por inasistencia (días)" variant="outlined" value={c.penalizacion_dias}
                    helperText="Si alguien falta sin avisar, cuántos días no puede volver a reservar. Vacío = sin penalización." inputProps={{ inputMode: "numeric", maxLength: 3 }}
                    onChange={(e) => set("penalizacion_dias", soloDigitos(e.target.value))} />
                </div>
                <Switch checked={c.requiere_aprobacion} onChange={(v) => set("requiere_aprobacion", v)}
                  titulo="Requiere aprobación" ayuda="La cita no queda confirmada hasta que un gestor o quien atiende la apruebe. Sirve para revisar antes cada pedido." />
                <Switch checked={c.permite_lista_espera} onChange={(v) => set("permite_lista_espera", v)}
                  titulo="Lista de espera" ayuda="Si un turno está lleno, la persona puede anotarse en espera. Si alguien cancela, el primero en espera toma el lugar y recibe un aviso." />
              </div>
            </Seccion>

            {/* ===================== Público ===================== */}
            <Seccion titulo="Público dirigido" sub={ETIQUETA_PUBLICO[c.publico_tipo]} abierta obligatoria={c.publico_tipo === "LISTADO"}>
              <div className="agenda-form-col">
                <select className="agenda-select" value={c.publico_tipo} onChange={(e) => set("publico_tipo", e.target.value as TipoPublico)}>
                  {(Object.keys(ETIQUETA_PUBLICO) as TipoPublico[]).map((k) => (
                    <option key={k} value={k}>{ETIQUETA_PUBLICO[k]}</option>
                  ))}
                </select>
                <Ayuda>{AYUDA_PUBLICO[c.publico_tipo]}</Ayuda>
                {c.publico_tipo !== "LISTADO" && (
                  <>
                    <div className="agenda-card-sub" style={{ margin: 0 }}>
                      Filtros opcionales para achicar el público. Si cargás varios, la persona tiene que cumplir todos. Ej.: cargo
                      "Analista" y departamento "RRHH" = solo los analistas de RRHH.
                    </div>
                    <MuiAutocomplete multiple options={catalogos.cargos} value={cargos} onChange={(_, v) => setCargos(v)}
                      filterSelectedOptions noOptionsText="Sin coincidencias"
                      renderInput={(params) => <TextField {...params} variant="outlined" label="Cargos" helperText="Solo estos cargos. Vacío = todos." />} />
                    <MuiAutocomplete multiple options={catalogos.departamentos} value={departamentos} onChange={(_, v) => setDepartamentos(v)}
                      filterSelectedOptions noOptionsText="Sin coincidencias"
                      renderInput={(params) => <TextField {...params} variant="outlined" label="Departamentos" helperText="Solo estos departamentos. Vacío = todos." />} />
                    <MuiAutocomplete multiple options={catalogos.unidades} value={unidades} onChange={(_, v) => setUnidades(v)}
                      filterSelectedOptions noOptionsText="Sin coincidencias"
                      renderInput={(params) => <TextField {...params} variant="outlined" label="Unidades de negocio" helperText="Solo estas unidades. Vacío = todas." />} />
                  </>
                )}

                <div className="agenda-item">
                  <div className="agenda-item-header">
                    <span>
                      {c.publico_tipo === "LISTADO" ? "Personas habilitadas" : "Además, habilitar a estas personas"} ({listado.length})
                      {c.publico_tipo === "LISTADO" && <Obligatorio />}
                    </span>
                    {listado.length > 0 && (
                      <IonButton fill="clear" size="small" color="medium" onClick={() => setListado([])}>Vaciar</IonButton>
                    )}
                  </div>
                  <Ayuda>
                    {c.publico_tipo === "LISTADO"
                      ? "Solo estas personas ven la agenda y pueden reservar."
                      : "Personas que pueden reservar aunque no entren en el público elegido arriba."}
                  </Ayuda>
                  <AgendaBuscadorFuncionario label="Buscar y agregar" size="small"
                    onElegir={(f) => setListado((prev) => (prev.some((p) => p.cedula === f.cedula) ? prev : [...prev, { cedula: f.cedula, nombre: f.nombre }]))} />
                  <ListaPersonas personas={listado} onQuitar={(ced) => setListado((prev) => prev.filter((p) => p.cedula !== ced))} />
                </div>

                <div className="agenda-item">
                  <div className="agenda-item-header"><span>Excluir a estas personas ({excluidas.length})</span></div>
                  <Ayuda>Estas personas no ven la agenda aunque entren en el público elegido.</Ayuda>
                  <AgendaBuscadorFuncionario label="Buscar y excluir" size="small"
                    onElegir={(f) => setExcluidas((prev) => (prev.some((p) => p.cedula === f.cedula) ? prev : [...prev, { cedula: f.cedula, nombre: f.nombre }]))} />
                  <ListaPersonas personas={excluidas} onQuitar={(ced) => setExcluidas((prev) => prev.filter((p) => p.cedula !== ced))} />
                </div>
              </div>
            </Seccion>

            {/* ===================== Formulario ===================== */}
            <Seccion titulo={`Preguntas al reservar (${preguntas.length})`} sub="Opcional: datos que la persona completa al reservar, como el motivo de la consulta o un teléfono.">
              {preguntas.map((p, i) => (
                <div key={p._k} className="agenda-item">
                  <div className="agenda-item-header">
                    <span>Pregunta {i + 1}</span>
                    <IonButton fill="clear" size="small" color="danger" onClick={() => setPreguntas((prev) => prev.filter((x) => x._k !== p._k))}>
                      <IonIcon slot="icon-only" icon={trashOutline} />
                    </IonButton>
                  </div>
                  <div className="agenda-form-col" style={{ gap: 10 }}>
                    <TextField size="small" label="Pregunta" required variant="outlined" fullWidth value={p.texto} placeholder="Ej.: Motivo de la consulta"
                      inputProps={{ maxLength: 300 }} helperText="Lo que se le pregunta a la persona al reservar."
                      onChange={(e) => editarPreg(p._k, { texto: e.target.value })} />
                    <div className="agenda-form-fila" style={{ alignItems: "center" }}>
                      <select className="agenda-select chico" value={p.tipo}
                        onChange={(e) => editarPreg(p._k, { tipo: e.target.value as "TEXTO" | "OPCION" })}>
                        <option value="TEXTO">Respuesta libre</option>
                        <option value="OPCION">Elegir una opción</option>
                      </select>
                      <Switch checked={p.obligatoria} onChange={(v) => editarPreg(p._k, { obligatoria: v })} titulo="Obligatoria" />
                    </div>
                    <Ayuda>
                      {p.tipo === "TEXTO" ? "Respuesta libre: la persona escribe lo que quiera." : "Elegir una opción: la persona elige de una lista."}
                      {p.obligatoria ? " Es obligatoria: no puede reservar sin responderla." : " Es opcional: puede dejarla vacía."}
                    </Ayuda>
                    {p.tipo === "OPCION" && (
                      <TextField size="small" variant="outlined" fullWidth multiline minRows={2} label="Opciones (una por línea)" required helperText="Escribí al menos 2 opciones, cada una en un renglón distinto."
                        value={p.opciones.join("\n")} onChange={(e) => editarPreg(p._k, { opciones: e.target.value.split("\n") })} />
                    )}
                  </div>
                </div>
              ))}
              {preguntas.length < 15 && (
                <IonButton fill="outline" expand="block"
                  onClick={() => setPreguntas((prev) => [...prev, { _k: nuevaClave(), texto: "", tipo: "TEXTO", opciones: [], obligatoria: false }])}>
                  <IonIcon slot="start" icon={addOutline} /> Agregar pregunta
                </IonButton>
              )}
            </Seccion>

            {/* ===================== Notificaciones ===================== */}
            <Seccion titulo="Notificaciones" sub="Avisos push a los usuarios y a los responsables.">
              <div className="agenda-form-col">
                <Switch checked={c.notificar_publicacion} onChange={(v) => set("notificar_publicacion", v)}
                  titulo="Avisar al público cuando se publique"
                  ayuda="Les llega un aviso al celular a quienes pueden reservar. Si cargaste apertura de inscripciones, el aviso sale ese día." />
                <div className="agenda-form-fila">
                  <TextField label="1er recordatorio (horas antes)" variant="outlined" value={c.recordatorio_1_horas}
                    helperText="Aviso al celular antes de la cita. Ej.: 24 = el día anterior. Vacío = sin aviso." inputProps={{ inputMode: "numeric", maxLength: 3 }}
                    onChange={(e) => set("recordatorio_1_horas", soloDigitos(e.target.value))} />
                  <TextField label="2do recordatorio (horas antes)" variant="outlined" value={c.recordatorio_2_horas}
                    inputProps={{ inputMode: "numeric", maxLength: 3 }} helperText="Un segundo aviso, más cerca de la cita. Ej.: 2 = dos horas antes."
                    onChange={(e) => set("recordatorio_2_horas", soloDigitos(e.target.value))} />
                </div>
                <Switch checked={c.notificar_responsable} onChange={(v) => set("notificar_responsable", v)}
                  titulo="Avisar al responsable interno" ayuda="Quien atiende recibe un aviso cada vez que alguien reserva, cambia el horario o cancela." />
                <Switch checked={c.notificar_correo} onChange={(v) => set("notificar_correo", v)}
                  titulo="Enviar también por correo al responsable" ayuda="Además del aviso en la app, le llega un correo. Al interno, a su correo de la empresa; al externo, al correo cargado." />
              </div>
            </Seccion>

            {/* ===================== Después / privacidad ===================== */}
            <Seccion titulo="Después de la cita y privacidad">
              <div className="agenda-form-col">
                <Switch checked={c.encuesta_habilitada} onChange={(v) => set("encuesta_habilitada", v)}
                  titulo="Encuesta de satisfacción" ayuda="Al terminar la cita, la persona puede calificarla de 1 a 5 y dejar un comentario. Es opcional para ella." />
                <Switch checked={c.confidencial} onChange={(v) => set("confidencial", v)}
                  titulo="Agenda confidencial"
                  ayuda="Para temas sensibles (por ej. psicólogo): solo quien atiende ve las respuestas de la persona y los avisos no dicen de qué es la cita." />
              </div>
            </Seccion>

            {/* ===================== Gestores ===================== */}
            <Seccion titulo={`Co-gestores (${gestores.length})`} sub="Pueden editar la agenda, aprobar, cancelar y marcar asistencia.">
              <div className="agenda-form-col">
                {creadoPor && <div className="agenda-card-sub" style={{ margin: 0 }}>El creador siempre es gestor.</div>}
                <AgendaBuscadorFuncionario label="Agregar co-gestor" helperText="Personas que administran esta agenda junto con vos."
                  onElegir={(f) => setGestores((prev) => (prev.some((p) => p.cedula === f.cedula) || f.cedula === creadoPor ? prev : [...prev, { cedula: f.cedula, nombre: f.nombre }]))} />
                <ListaPersonas personas={gestores} onQuitar={(ced) => setGestores((prev) => prev.filter((p) => p.cedula !== ced))} />
              </div>
            </Seccion>

            {esBorrador ? (
              <>
                <div className="agenda-aviso info">
                  <strong>Guardar borrador:</strong> queda preparada y no la ve nadie más que los gestores.{" "}
                  <strong>Publicar:</strong> el público la ve en el inicio de su app y puede reservar.
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 24 }}>
                  <IonButton fill="outline" style={{ flex: "1 1 160px" }} onClick={() => guardar(false)} disabled={guardando}>
                    {guardando ? "Guardando..." : "Guardar borrador"}
                  </IonButton>
                  <IonButton style={{ flex: "1 1 160px" }} onClick={() => guardar(true)} disabled={guardando}>
                    {guardando ? "Guardando..." : "Publicar"}
                  </IonButton>
                </div>
              </>
            ) : (
              <>
                {tieneReservas && (
                  <div className="agenda-aviso warning">
                    Cambiar horarios no afecta las citas ya tomadas: siguen vigentes en su fecha y hora.
                  </div>
                )}
                <IonButton expand="block" onClick={() => guardar(false)} disabled={guardando} style={{ marginBottom: 24 }}>
                  {guardando ? "Guardando..." : "Guardar cambios"}
                </IonButton>
              </>
            )}
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default AgendaTemaForm;
